/**
 * Tenant store — holds the branding + feature config for the current tenant.
 *
 * Phase 3 Part 2: Per-tenant branding + feature flags.
 *
 * Data flow:
 *   App boot → fetchTenantConfig() → GET /api/v1/tenants/config (PUBLIC)
 *     → stored in Zustand + mirrored to localStorage so subsequent cold
 *       boots have branding available before the network call returns.
 *
 * Tier → default feature matrix (can be overridden per-tenant in config.features):
 *   shift    ($49):  none
 *   terrain  ($149): terrain, nfc
 *   command  ($399): terrain, nfc, incident, command, api
 *
 * The theme store's `tenantPrimary` acts as the base accent that user
 * preference sits on top of. If the user hasn't set a custom accent,
 * the tenant primary wins.
 */
import { create } from 'zustand'
import { workerGetV1, workerPutV1 } from '../api/worker'
import { applyTenantPrimary } from './theme'

export type TenantTier = 'shift' | 'terrain' | 'command' | 'starter' | 'professional' | 'enterprise'

export interface TenantFeatures {
  terrain:  boolean  // Terrain tracking (Phase 5+)
  nfc:      boolean  // NFC checkpoint scanning
  incident: boolean  // Incident reporting module
  command:  boolean  // Command center dashboards
  api:      boolean  // External API access
}

export interface TenantConfig {
  /** Display name shown in the top bar. */
  name?: string
  /** Primary brand color (hex). Becomes the base accent. */
  primary?: string
  /** Optional secondary color for gradients. */
  primaryLight?: string
  /** URL to tenant logo (shown instead of the default shield). */
  logoUrl?: string
  /** Per-tenant feature overrides — merged on top of tier defaults. */
  features?: Partial<TenantFeatures>
}

export interface Tenant {
  id:     string
  name:   string
  tier:   TenantTier
  config: TenantConfig
}

const STORAGE_KEY = 'castellan_tenant_config'

/* ── Tier → default features ─────────────────────────────────────────── */

export function defaultFeaturesForTier(tier: TenantTier): TenantFeatures {
  // Legacy tiers from D1 seed ('starter', 'professional', 'enterprise') map
  // onto the current Shift / Terrain / Command names.
  const normalized: string =
    tier === 'starter'      ? 'shift' :
    tier === 'professional' ? 'terrain' :
    tier === 'enterprise'   ? 'command' :
    tier

  switch (normalized) {
    case 'command':
      return { terrain: true,  nfc: true,  incident: true,  command: true,  api: true  }
    case 'terrain':
      return { terrain: true,  nfc: true,  incident: false, command: false, api: false }
    case 'shift':
    default:
      return { terrain: false, nfc: false, incident: false, command: false, api: false }
  }
}

/** Merge tier defaults with any per-tenant feature overrides. */
export function resolveFeatures(tenant: Tenant | null): TenantFeatures {
  if (!tenant) return defaultFeaturesForTier('shift')
  const base = defaultFeaturesForTier(tenant.tier)
  const overrides = tenant.config.features ?? {}
  return { ...base, ...overrides }
}

/* ── Persistence ─────────────────────────────────────────────────────── */

function loadCached(): Tenant | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Tenant
  } catch { return null }
}

function saveCached(t: Tenant) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(t)) } catch { /* silent */ }
}

/* ── Store ───────────────────────────────────────────────────────────── */

interface TenantState {
  tenant:  Tenant | null
  loading: boolean
  error:   string | null

  features: TenantFeatures
  hasFeature: (name: keyof TenantFeatures) => boolean

  /** Fetch the current tenant's config from the Worker. Call on boot. */
  fetchConfig: () => Promise<void>

  /** Update tenant config (exec/dev only). Merges into existing JSON. */
  updateConfig: (patch: Partial<TenantConfig>, name?: string) => Promise<boolean>
}

const initialTenant = loadCached()

export const useTenantStore = create<TenantState>((set, get) => ({
  tenant:   initialTenant,
  loading:  false,
  error:    null,
  features: resolveFeatures(initialTenant),

  hasFeature: (name) => get().features[name] === true,

  async fetchConfig() {
    set({ loading: true, error: null })
    try {
      const res = await workerGetV1<Tenant>('/api/v1/tenants/config')
      saveCached(res)
      set({
        tenant:   res,
        features: resolveFeatures(res),
        loading:  false,
      })
      // Push tenant primary into the theme as a base layer (no‑op if the
      // user already picked a custom accent).
      applyTenantPrimary(res.config.primary)
    } catch (err) {
      set({
        loading: false,
        error:   err instanceof Error ? err.message : 'Failed to load tenant config',
      })
    }
  },

  async updateConfig(patch, name) {
    try {
      const res = await workerPutV1<{ success: boolean; config: TenantConfig; error?: string }>(
        '/api/v1/tenants/config',
        { config: patch, ...(name ? { name } : {}) },
      )
      if (!res.success) return false
      const current = get().tenant
      if (current) {
        const updated: Tenant = {
          ...current,
          name:   name ?? current.name,
          config: res.config ?? { ...current.config, ...patch },
        }
        saveCached(updated)
        set({ tenant: updated, features: resolveFeatures(updated) })
        applyTenantPrimary(updated.config.primary)
      }
      return true
    } catch {
      return false
    }
  },
}))
